import React, { useEffect, useState, useMemo } from "react";
import { api } from "../../api";
import { motion } from "framer-motion";
import DepositPopup from "./DepositPopup";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeposit, setShowDeposit] = useState(false);
  const [searchPhone, setSearchPhone] = useState("");

  const isSearching = searchPhone.trim().length > 0;

  const fetchOrders = async (pageNumber = 1, phone = "") => {
    try {
      const res = await api.get("/orders-list", {
        params: { page: pageNumber, pageSize, phone },
      });

      const ordersArray = Array.isArray(res.data.orders?.orders)
        ? res.data.orders.orders
        : [];

      setOrders(ordersArray);
      setTotalPages(res.data.orders?.totalPages || 1);
      setPage(pageNumber);
    } catch (err) {
      console.error(
        "Error fetching orders:",
        err.response?.data || err.message,
      );
      setOrders([]);
      setTotalPages(1);
    }
  };

  useEffect(() => {
    if (isSearching) {
      fetchOrders(1, searchPhone);
    } else {
      fetchOrders(page);
    }
  }, [page, searchPhone]);

  const handleApprove = async (id) => {
    try {
      await api.post("/deposits/approve", { id });

      fetchOrders(page);
    } catch (err) {
      console.error(err);
      alert("Failed to approve deposit");
    }
  };

  const handleReject = async (id) => {
    try {
      await api.post("/deposits/reject", { id });

      fetchOrders(page);
    } catch (err) {
      console.error(err);
      alert("Failed to reject deposit");
    }
  };

  return (
    <motion.div
      className="bg-black/40 p-6 rounded-2xl shadow-lg relative"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {/* Deposit Now Button */}
      <div className="flex justify-end mb-4 mt-8 md:mt-2">
        <button
          onClick={() => setShowDeposit(true)}
          className="bg-gradient-to-r from-[#4e6d8c] to-[#6784a4] hover:opacity-90 text-white px-5 py-2 sm:py-3 rounded-md font-semibold text-sm sm:text-base transition"
        >
          Deposit Now
        </button>
      </div>

      {/* 🔍 Phone Search */}
      <div className="mb-4 flex justify-end">
        <input
          type="text"
          placeholder="Search by phone number..."
          value={searchPhone}
          onChange={(e) => setSearchPhone(e.target.value)}
          className="w-full md:w-1/4 px-4 py-2 rounded bg-black/60 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
        />
      </div>

      {/* Orders Table */}
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-gray-600">
            <th className="p-2">Phone</th>
            <th className="p-2">Payer</th>
            <th className="p-2">Tx Ref</th>
            <th className="p-2">Amount</th>
            <th className="p-2">Status</th>
            <th className="p-2">Date</th>
          </tr>
        </thead>
        <tbody>
          {orders.length === 0 ? (
            <tr>
              <td colSpan={6} className="text-center py-4 text-gray-400">
                No orders found.
              </td>
            </tr>
          ) : (
            orders.map((o) => (
              <tr key={o.id} className="border-b border-gray-700">
                <td className="p-2">{o.phoneNumber}</td>

                <td className="p-2">{o.payerName || "-"}</td>

                <td className="p-2 text-xs">{o.txRef}</td>

                <td className="p-2">{o.amount} ETB</td>

                <td className="p-2">
                  {o.status === "PENDING" ? (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApprove(o.id)}
                        className="bg-yellow-600 px-3 py-1 rounded text-black font-semibold hover:bg-yellow-500"
                      >
                        Approve
                      </button>

                      <button
                        onClick={() => handleReject(o.id)}
                        className="bg-red-600 px-3 py-1 rounded text-white font-semibold hover:bg-red-500"
                      >
                        Reject
                      </button>
                    </div>
                  ) : o.status === "COMPLETED" ? (
                    <span className="bg-green-600 px-3 py-1 rounded text-white font-semibold">
                      Completed
                    </span>
                  ) : (
                    <span className="bg-red-600 px-3 py-1 rounded text-white font-semibold">
                      Rejected
                    </span>
                  )}
                </td>

                <td className="p-2">{o.createdAt?.split("T")[0]}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="flex justify-between mt-4">
        <button
          disabled={isSearching || page === 1}
          onClick={() => setPage((p) => p - 1)}
          className="bg-blue-700 px-4 py-2 rounded disabled:opacity-50"
        >
          Previous
        </button>

        <span className="text-gray-300 px-2 py-2">
          {isSearching ? "Search results" : `Page ${page} of ${totalPages}`}
        </span>

        <button
          disabled={isSearching || page === totalPages}
          onClick={() => setPage((p) => p + 1)}
          className="bg-blue-700 px-4 py-2 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>

      {/* Deposit Popup */}
      {showDeposit && (
        <DepositPopup
          onClose={() => setShowDeposit(false)}
          onSuccess={() => fetchOrders(page)} // ✅ fixed callback
        />
      )}
    </motion.div>
  );
}
